import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { analyticsManager } from './manager';
import { PostHogProvider as PHProvider, type PostHogConfig } from './providers/posthog';
import { ConsoleProvider } from './providers/console';
import { GlobalCapture, ANALYTICS_ROUTE_CHANGE } from './GlobalCapture';
import { pageContext, classifyPath } from './pageContext';
import type { AnalyticsProvider } from './types';

type ManagerType = typeof analyticsManager;

const AnalyticsContext = createContext<ManagerType>(analyticsManager);

export function useAnalytics(): ManagerType {
  return useContext(AnalyticsContext);
}

interface AnalyticsProviderProps {
  children: ReactNode;
  providers?: AnalyticsProvider[];
}

export function AnalyticsContextProvider({ children, providers }: AnalyticsProviderProps) {
  const initialized = useRef(false);

  if (!initialized.current && typeof window !== 'undefined') {
    initialized.current = true;

    if (providers) {
      for (const p of providers) analyticsManager.addProvider(p);
    } else {
      const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
      if (posthogKey) {
        const ph = new PHProvider();
        ph.init({
          apiKey: posthogKey,
          apiHost: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || '/e',
          defaults: '2026-08-30',
        } satisfies PostHogConfig);
        analyticsManager.addProvider(ph);
      }

      if (import.meta.env.DEV && !import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
        analyticsManager.addProvider(new ConsoleProvider());
      }
    }
  }

  return (
    <AnalyticsContext.Provider value={analyticsManager}>
      <RouteChangeTracker />
      <GlobalCapture />
      {children}
    </AnalyticsContext.Provider>
  );
}

function RouteChangeTracker() {
  const router = useRouter();
  const previousPath = useRef<string | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', ({ toLocation }) => {
      const fromPath = previousPath.current;
      analyticsManager.register(pageContext());
      analyticsManager.pageView(toLocation.href, {
        path: toLocation.pathname,
        search: toLocation.searchStr,
        hash: toLocation.hash,
        previous_path: fromPath,
        navigation_type: fromPath ? 'spa' : 'load',
      });
      if (!fromPath) {
        analyticsManager.setPersonProperties({
          first_section: classifyPath(toLocation.pathname).section,
        });
      }
      previousPath.current = toLocation.pathname;
      if (fromPath) {
        window.dispatchEvent(new Event(ANALYTICS_ROUTE_CHANGE));
      }
    });
    return unsubscribe;
  }, [router]);

  return null;
}
